/**
 * Album page design variants — for live A/B tasting.
 *
 * Each entry returns either `null` (use the legacy `C` palette as-is) or an override
 * object whose keys components consume directly. Missing keys fall back to `C`.
 *
 * Each variant defines colors for ALL views — MY_ALBUM, USER_ALBUM, TRADE_MATCH,
 * TRADE_REVIEW — so the chosen theme stays cohesive across the entire flow.
 *
 * Trade-state colors:
 *   tradeReceiveBg    — sticker the trade brings to you (you receive / they have, you need)
 *   tradeGiveBg       — sticker the trade takes from you (you give / you have, they need)
 *   tradeBothBg       — both of you already own this sticker
 *   tradeNeitherBg    — neither owns this sticker
 *   tradeHeaderBg     — TRADE_MATCH action bar background
 *   tradeReviewHeaderBg — TRADE_REVIEW action bar background
 *   tradeSummaryBg    — floating "trade summary" pill at the bottom
 */

export const ALBUM_VARIANT_NAMES = [
  'Country Colors',
  'Neutral Chrome, Green Stickers + SmartTrade',
  'No Green Anywhere',
  'Black Buttons (Mono)',
  'Classic Trade (Green / Red)',
  'Cool Ocean (Cyan / Indigo)',
];

/* Shared neutral right-panel tokens. */
const NEUTRAL_PANEL = {
  searchInputBg:    '#f3f4f6',
  searchInputText:  '#1f2937',
  searchIconColor:  '#6b7280',
  searchHoverBg:    '#e5e7eb',
  searchPlaceholderTextColor: '#9ca3af',
};

/* Default trade palette — amber/purple/slate, used by the legacy variants. */
const DEFAULT_TRADE = {
  tradeReceiveBg:     '#F59E0B',
  tradeReceiveText:   '#ffffff',
  tradeReceiveStrong: '#B45309',
  tradeGiveBg:        '#8B5CF6',
  tradeGiveText:      '#ffffff',
  tradeGiveStrong:    '#6D28D9',
  tradeBothBg:        '#94A3B8',
  tradeBothText:      '#ffffff',
  tradeBothStrong:    '#475569',
  tradeNeitherBg:     'transparent',
  tradeNeitherText:   '#CBD5E1',
  tradeNeitherBorder: '#CBD5E1',
  tradeHeaderBg:      '#FFFBEB',
  tradeHeaderText:    '#78350f',
  tradeHeaderBorder:  '#fde68a33',
  tradeReviewHeaderBg:     '#FFFBEB',
  tradeReviewHeaderText:   '#78350f',
  tradeReviewHeaderBorder: '#fde68a33',
  tradeSummaryBg:     '#1e1b4b',
  tradeSummaryText:   '#ffffff',
  tradeOwnedDimBg:    '#10B98133',
  tradeOwnedDimText:  '#065f46',
  tradeSelectedOutline: '#1e293b',
  tradeSelectedShadow:  'rgba(30,41,59,0.4)',
};

export function getAlbumTheme(variant) {
  switch (variant) {
    case 1:
      // Neutral chrome (white / light grey) but owned stickers + SmartTrade stay green.
      return {
        ...DEFAULT_TRADE,
        pageBg: '#f5f5f5',
        actionBarBg: '#ffffff',
        actionBarBorder: '#e5e7eb',
        actionBarText: '#1f2937',
        rightPanelBg: '#fafafa',
        rightPanelBorder: '#e5e7eb',
        gridBg: 'rgba(255,255,255,0.6)',
        stickerOwnedBg: '#10B981',
        stickerOwnedText: '#ffffff',
        stickerMissingText: '#10B981',
        stickerMissingBorder: '#10B981',
        stickerSelectedOutline: '#064e3b',
        stickerSelectedShadow: 'rgba(6,78,59,0.4)',
        stickerBadgeBg: '#064e3b',
        primaryAccent: '#10B981',
        primaryAccentDim: 'rgba(16,185,129,0.14)',
        progressTrack: '#e5e7eb',
        mutedText: '#6b7280',
        ...NEUTRAL_PANEL,
      };

    case 2:
      // No green at all — owned stickers in blue, trade colors stay amber/purple to avoid green.
      return {
        ...DEFAULT_TRADE,
        pageBg: '#f5f5f5',
        actionBarBg: '#ffffff',
        actionBarBorder: '#e5e7eb',
        actionBarText: '#1f2937',
        rightPanelBg: '#fafafa',
        rightPanelBorder: '#e5e7eb',
        gridBg: 'rgba(255,255,255,0.6)',
        stickerOwnedBg: '#3B82F6',
        stickerOwnedText: '#ffffff',
        stickerMissingText: '#3B82F6',
        stickerMissingBorder: '#3B82F6',
        stickerSelectedOutline: '#1E3A8A',
        stickerSelectedShadow: 'rgba(30,58,138,0.4)',
        stickerBadgeBg: '#1E3A8A',
        primaryAccent: '#3B82F6',
        primaryAccentDim: 'rgba(59,130,246,0.14)',
        progressTrack: '#e5e7eb',
        mutedText: '#6b7280',
        // override owned-dim so it isn't green either
        tradeOwnedDimBg: 'rgba(59,130,246,0.18)',
        tradeOwnedDimText: '#1E3A8A',
      };

    case 3:
      // Black Buttons — minimal monochrome. All buttons / chrome are black,
      // and the trade palette is dark grayscale + a single white-on-black accent.
      return {
        pageBg: '#fafafa',
        actionBarBg: '#ffffff',
        actionBarBorder: '#e5e7eb',
        actionBarText: '#0a0a0a',
        rightPanelBg: '#ffffff',
        rightPanelBorder: '#e5e7eb',
        gridBg: '#ffffff',
        stickerOwnedBg: '#0a0a0a',
        stickerOwnedText: '#ffffff',
        stickerMissingText: '#0a0a0a',
        stickerMissingBorder: '#0a0a0a',
        stickerSelectedOutline: '#0a0a0a',
        stickerSelectedShadow: 'rgba(10,10,10,0.5)',
        stickerBadgeBg: '#0a0a0a',
        primaryAccent: '#0a0a0a',
        primaryAccentDim: 'rgba(10,10,10,0.08)',
        progressTrack: '#e5e7eb',
        mutedText: '#525252',
        ...NEUTRAL_PANEL,
        // Trade palette — black/white/grays only
        tradeReceiveBg: '#0a0a0a',
        tradeReceiveText: '#ffffff',
        tradeReceiveStrong: '#000000',
        tradeGiveBg: '#525252',
        tradeGiveText: '#ffffff',
        tradeGiveStrong: '#262626',
        tradeBothBg: '#d4d4d4',
        tradeBothText: '#0a0a0a',
        tradeBothStrong: '#525252',
        tradeNeitherBg: 'transparent',
        tradeNeitherText: '#a3a3a3',
        tradeNeitherBorder: '#d4d4d4',
        tradeHeaderBg: '#fafafa',
        tradeHeaderText: '#0a0a0a',
        tradeHeaderBorder: '#e5e7eb',
        tradeReviewHeaderBg: '#fafafa',
        tradeReviewHeaderText: '#0a0a0a',
        tradeReviewHeaderBorder: '#e5e7eb',
        tradeSummaryBg: '#0a0a0a',
        tradeSummaryText: '#ffffff',
        tradeOwnedDimBg: '#f5f5f5',
        tradeOwnedDimText: '#525252',
        tradeSelectedOutline: '#0a0a0a',
        tradeSelectedShadow: 'rgba(10,10,10,0.5)',
        // Smart trade card uses the black look
        smartTradeCardBg:    '#ffffff',
        smartTradeCardBorder:'#e5e7eb',
        smartTradeButtonBg:  '#0a0a0a',
        smartTradeButtonText:'#ffffff',
      };

    case 4:
      // Classic Trade — green album + green/red trade colors (semantic: green=receive, red=give).
      return {
        pageBg: '#f5f5f5',
        actionBarBg: '#ffffff',
        actionBarBorder: '#e5e7eb',
        actionBarText: '#1f2937',
        rightPanelBg: '#fafafa',
        rightPanelBorder: '#e5e7eb',
        gridBg: 'rgba(255,255,255,0.6)',
        stickerOwnedBg: '#10B981',
        stickerOwnedText: '#ffffff',
        stickerMissingText: '#10B981',
        stickerMissingBorder: '#10B981',
        stickerSelectedOutline: '#064e3b',
        stickerSelectedShadow: 'rgba(6,78,59,0.4)',
        stickerBadgeBg: '#064e3b',
        primaryAccent: '#10B981',
        primaryAccentDim: 'rgba(16,185,129,0.14)',
        progressTrack: '#e5e7eb',
        mutedText: '#6b7280',
        ...NEUTRAL_PANEL,
        tradeReceiveBg: '#10B981',
        tradeReceiveText: '#ffffff',
        tradeReceiveStrong: '#065f46',
        tradeGiveBg: '#EF4444',
        tradeGiveText: '#ffffff',
        tradeGiveStrong: '#991B1B',
        tradeBothBg: '#94A3B8',
        tradeBothText: '#ffffff',
        tradeBothStrong: '#475569',
        tradeNeitherBg: 'transparent',
        tradeNeitherText: '#CBD5E1',
        tradeNeitherBorder: '#CBD5E1',
        tradeHeaderBg: '#ECFDF5',
        tradeHeaderText: '#065f46',
        tradeHeaderBorder: '#a7f3d033',
        tradeReviewHeaderBg: '#FEF2F2',
        tradeReviewHeaderText: '#7f1d1d',
        tradeReviewHeaderBorder: '#fecaca33',
        tradeSummaryBg: '#064e3b',
        tradeSummaryText: '#ffffff',
        tradeOwnedDimBg: 'rgba(16,185,129,0.18)',
        tradeOwnedDimText: '#065f46',
        tradeSelectedOutline: '#064e3b',
        tradeSelectedShadow: 'rgba(6,78,59,0.4)',
      };

    case 5:
      // Cool Ocean — cyan and indigo. Calm and tech-y.
      return {
        pageBg: '#f0f9ff',
        actionBarBg: '#ffffff',
        actionBarBorder: '#bae6fd',
        actionBarText: '#0c4a6e',
        rightPanelBg: '#f0f9ff',
        rightPanelBorder: '#bae6fd',
        gridBg: 'rgba(255,255,255,0.7)',
        stickerOwnedBg: '#06B6D4',
        stickerOwnedText: '#ffffff',
        stickerMissingText: '#06B6D4',
        stickerMissingBorder: '#06B6D4',
        stickerSelectedOutline: '#0c4a6e',
        stickerSelectedShadow: 'rgba(12,74,110,0.4)',
        stickerBadgeBg: '#0c4a6e',
        primaryAccent: '#06B6D4',
        primaryAccentDim: 'rgba(6,182,212,0.14)',
        progressTrack: '#bae6fd',
        mutedText: '#0369a1',
        tradeReceiveBg: '#06B6D4',
        tradeReceiveText: '#ffffff',
        tradeReceiveStrong: '#0E7490',
        tradeGiveBg: '#6366F1',
        tradeGiveText: '#ffffff',
        tradeGiveStrong: '#3730A3',
        tradeBothBg: '#A5F3FC',
        tradeBothText: '#0c4a6e',
        tradeBothStrong: '#0E7490',
        tradeNeitherBg: 'transparent',
        tradeNeitherText: '#BAE6FD',
        tradeNeitherBorder: '#BAE6FD',
        tradeHeaderBg: '#ECFEFF',
        tradeHeaderText: '#0c4a6e',
        tradeHeaderBorder: '#a5f3fc55',
        tradeReviewHeaderBg: '#EEF2FF',
        tradeReviewHeaderText: '#3730A3',
        tradeReviewHeaderBorder: '#c7d2fe55',
        tradeSummaryBg: '#0c4a6e',
        tradeSummaryText: '#ffffff',
        tradeOwnedDimBg: 'rgba(6,182,212,0.18)',
        tradeOwnedDimText: '#0E7490',
        tradeSelectedOutline: '#0c4a6e',
        tradeSelectedShadow: 'rgba(12,74,110,0.4)',
      };

    case 0:
      // Country Colors — cada figurita se pinta con el color de su país
      // (mismo color que ya muestra la tarjeta del país en country-cards).
      // Chrome neutro para que la grilla cargue todo el peso visual; trade
      // queda con la paleta clásica verde/rojo.
      return {
        pageBg: '#f5f5f5',
        actionBarBg: '#ffffff',
        actionBarBorder: '#e5e7eb',
        actionBarText: '#1f2937',
        rightPanelBg: '#fafafa',
        rightPanelBorder: '#e5e7eb',
        gridBg: 'rgba(255,255,255,0.7)',
        useCountryColors: true,
        stickerOwnedText: '#ffffff',
        stickerSelectedOutline: '#0f172a',
        stickerSelectedShadow: 'rgba(15,23,42,0.4)',
        stickerBadgeBg: '#0f172a',
        primaryAccent: '#10B981',
        primaryAccentDim: 'rgba(16,185,129,0.14)',
        progressTrack: '#e5e7eb',
        mutedText: '#6b7280',
        ...NEUTRAL_PANEL,
        tradeReceiveBg: '#10B981',
        tradeReceiveText: '#ffffff',
        tradeReceiveStrong: '#065f46',
        tradeGiveBg: '#EF4444',
        tradeGiveText: '#ffffff',
        tradeGiveStrong: '#991B1B',
        tradeBothBg: '#94A3B8',
        tradeBothText: '#ffffff',
        tradeBothStrong: '#475569',
        tradeNeitherBg: 'transparent',
        tradeNeitherText: '#CBD5E1',
        tradeNeitherBorder: '#CBD5E1',
        tradeHeaderBg: '#ECFDF5',
        tradeHeaderText: '#065f46',
        tradeHeaderBorder: '#a7f3d033',
        tradeReviewHeaderBg: '#FEF2F2',
        tradeReviewHeaderText: '#7f1d1d',
        tradeReviewHeaderBorder: '#fecaca33',
        tradeSummaryBg: '#0f172a',
        tradeSummaryText: '#ffffff',
        tradeOwnedDimBg: 'rgba(16,185,129,0.18)',
        tradeOwnedDimText: '#065f46',
        tradeSelectedOutline: '#0f172a',
        tradeSelectedShadow: 'rgba(15,23,42,0.4)',
      };

    default:
      return { ...DEFAULT_TRADE };
  }
}
